import { isUploadStorageKey, resolveStoredUploadPreview } from "lib/storedUploadImage";
import type { InstructorFormValues } from "./types";

function idsFromRelation(value: unknown): number[] {
  return Array.isArray(value)
    ? value
        .map((item) => (typeof item === "object" && item !== null ? (item as { id?: unknown }).id : item))
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0)
    : [];
}

export function instructorFormFromApi(row: Record<string, any>): InstructorFormValues {
  return {
    id: Number(row.id) || null,
    name: row.name || "",
    email: row.email || "",
    phone: row.phone || "",
    password: "",
    passwordConfirmation: "",
    image: isUploadStorageKey(row.image) ? row.image : "",
    imagePreview: resolveStoredUploadPreview(row.image, row.image_url),
    bio: row.bio || "",
    isActive: Boolean(row.is_active),
    stageIds: idsFromRelation(row.stage_ids ?? row.stages),
    gradeIds: idsFromRelation(row.grade_ids ?? row.grades),
    subjectIds: idsFromRelation(row.subject_ids ?? row.subjects),
  };
}

export function buildInstructorPayload(form: InstructorFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    phone: form.phone.trim(),
    email: form.email.trim() || null,
    image: form.image || null,
    bio: form.bio.trim() || null,
    // New instructors created by the admin are active immediately. The
    // backend enforces this too; keeping it here prevents a stale form value
    // from contradicting the create-flow contract.
    is_active: form.id === null ? true : form.isActive,
    stage_ids: form.stageIds,
    grade_ids: form.gradeIds,
    subject_ids: form.subjectIds,
  };

  if (form.password.trim()) {
    payload.password = form.password;
    payload.password_confirmation = form.passwordConfirmation;
  }

  return payload;
}

export function validateInstructorForm(form: InstructorFormValues, isArabic: boolean): string | null {
  if (!form.name.trim()) return isArabic ? "اكتب اسم المدرس." : "Enter the instructor name.";
  if (!form.phone.trim()) return isArabic ? "اكتب رقم الهاتف." : "Enter the phone number.";
  if (form.password && (form.password.length < 6 || form.password !== form.passwordConfirmation)) {
    return isArabic ? "تأكد من كلمة المرور وتأكيدها (6 أحرف على الأقل)." : "Confirm a matching password of at least 6 characters.";
  }

  const hasAssignment = form.stageIds.length + form.gradeIds.length + form.subjectIds.length > 0;

  return hasAssignment
    ? null
    : isArabic
      ? "اختر تعيينًا أكاديميًا واحدًا على الأقل."
      : "Choose at least one academic assignment.";
}
